// components/DocumentsPage.js - Gestion des documents avec permissions
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { Home, FileText, Upload, Download, Trash2, Lock, Globe, User, Clock } from 'lucide-react';

const DocumentsPage = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  
  useEffect(() => {
    // Use stable category keys for backend logic and display translated labels
    setCategories([
      { key: 'payslip', label: t('documents.category_payslip') },
      { key: 'contract', label: t('documents.category_contract') },
      { key: 'rules', label: t('documents.category_rules') },
      { key: 'quality', label: t('documents.category_quality') },
      { key: 'certificate', label: t('documents.category_certificate') },
      { key: 'diploma', label: t('documents.category_diploma') },
      { key: 'other_public', label: t('documents.category_other_public') },
      { key: 'other_private', label: t('documents.category_other_private') }
    ]);
  }, [t]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [designation, setDesignation] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const isAdmin = currentUser?.role === 'admin';
  const uploadInProgressRef = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([loadCurrentUser(), loadDocuments()]).then(() => {});
  }, []);

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      loadEmployees();
    }
  }, [currentUser]);

  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data.user || data);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateur:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/documents/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        console.error('Erreur chargement documents:', response.status);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB max
        alert('❌ ' + t('documents.error_file_too_large'));
        return;
      }
      setSelectedFile(file);
    }
  };

  const isOtherCategory = (categoryKey) => {
    return categoryKey === 'other_public' || categoryKey === 'other_private';
  };

  const getAccessType = (categoryKey) => {
    const publicCategoryKeys = ['rules', 'quality', 'other_public'];
    return publicCategoryKeys.includes(categoryKey) ? 'public' : 'private';
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCategory) {
      alert('❌ ' + t('documents.error_select_file_category'));
      return;
    }

    const accessType = getAccessType(selectedCategory);

    if (isOtherCategory(selectedCategory) && !designation.trim()) {
      alert('❌ ' + t('documents.error_designation_required'));
      return;
    }

    if (accessType === 'private' && !selectedEmployeeId) {
      alert('❌ ' + t('documents.error_employee_required'));
      return;
    }

    // Eviter double envoi (double-clic ou retard du state)
    if (uploadInProgressRef.current) return;
    uploadInProgressRef.current = true;
    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      // send both a stable category key and a translated label for backward compatibility
      const categoryObj = categories.find(c => c.key === selectedCategory);
      const categoryLabel = categoryObj ? categoryObj.label : selectedCategory;
      formData.append('category', categoryLabel);
      formData.append('categoryKey', selectedCategory);
      const documentName = isOtherCategory(selectedCategory)
        ? designation.trim()
        : selectedFile.name;
      formData.append('name', documentName);
      if (selectedEmployeeId) formData.append('employeeId', selectedEmployeeId);
      if (designation && isOtherCategory(selectedCategory)) {
        formData.append('designation', designation.trim());
      }

      const response = await fetch(`${API_BASE}/api/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      let data = null;
      try {
        const text = await response.text();
        if (text) data = JSON.parse(text);
      } catch (e) {
        console.warn('Upload response not JSON:', e);
      }

      const ok = response.ok && data && data.success;
      if (ok) {
        setSelectedFile(null);
        setSelectedCategory('');
        setSelectedEmployeeId('');
        setDesignation('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        await loadDocuments();
        alert('✅ ' + t('documents.success_uploaded'));
      } else {
        const msg = (data && data.error) || t('documents.error_upload');
        alert('❌ ' + msg);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('❌ ' + t('documents.error_upload'));
    } finally {
      setUploading(false);
      uploadInProgressRef.current = false;
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = doc.originalName || doc.name;
        try {
          window.document.body.appendChild(a);
          a.click();
        } finally {
          try { window.URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
          try {
            if (a && a.parentNode) a.parentNode.removeChild(a);
          } catch (err) {
            console.warn('Could not remove temporary download link:', err);
          }
        }
      } else {
        const data = await response.json();
        alert('❌ ' + (data.error || t('documents.error_download')));
      }
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('❌ ' + t('documents.error_download'));
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm(t('documents.delete_confirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('✅ ' + t('documents.success_deleted'));
        loadDocuments();
      } else {
        alert('❌ ' + (data.error || t('documents.error_delete')));
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ ' + t('documents.error_delete'));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 ' + t('documents.bytes');
    const k = 1024;
    const sizes = [t('documents.bytes'), t('documents.kb'), t('documents.mb'), t('documents.gb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getCategoryLabel = (categoryValue) => {
    // categoryValue may be a stable key (preferred) or a translated label (legacy)
    if (!categoryValue) return '';
    const found = categories.find(c => c.key === categoryValue);
    if (found) return found.label;
    // If it's already a label (from older records), return as-is
    return categoryValue;
  };

  const getRetentionInfo = (category) => {
    // Accept either stable keys (preferred) or translated labels (legacy)
    const isPayslip = category === 'payslip' || category === t('documents.category_payslip');
    if (isPayslip) {
      return t('documents.retention_payslip');
    }
    // Other categories are kept indefinitely unless specified
    if (isOtherCategory(category) || category === t('documents.category_other_public') || category === t('documents.category_other_private')) {
      return t('documents.retention_other');
    }
    return t('documents.retention_indeterminate');
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getDaysUntilExpiration = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>
            {t('documents.loading')}
          </div>
        </div>
      </div>
    );
  }

  const accessType = selectedCategory ? getAccessType(selectedCategory) : null;

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('documents.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FileText size={32} />
            {t('documents.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {isAdmin ? t('documents.subtitle_admin') : t('documents.subtitle_employee')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="documents-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>

      {/* Warning: limited storage duration (12 months) — visible to all (employee + manager) */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.15) 100%)',
        border: '1px solid rgba(245, 158, 11, 0.5)',
        borderRadius: '12px',
        padding: '14px 18px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
      }}>
        <span style={{ fontSize: '22px', flexShrink: 0 }} aria-hidden>⚠️</span>
        <div>
          <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: '4px', fontSize: '15px' }}>
            {t('documents.storage_warning_title')}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', lineHeight: 1.45 }}>
            {t('documents.storage_warning_message')}
          </div>
        </div>
      </div>

      {/* Upload Section - Admin seulement */}
      {isAdmin && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <h2 style={{
            color: '#ffffff',
            marginBottom: '20px',
            fontSize: '20px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Upload size={20} />
            {t('documents.upload_document')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8' }}>
                {t('documents.category_label')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  const accessType = getAccessType(e.target.value);
                  if (accessType === 'public') {
                    setSelectedEmployeeId('');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px'
                }}
              >
                <option value="">{t('documents.select_category')}</option>
                {categories.map(cat => (
                  <option key={cat.key} value={cat.key} style={{ color: '#000' }}>{cat.label}</option>
                ))}
              </select>
              {selectedCategory && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {accessType === 'public' ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Globe size={14} /> {t('documents.accessible_all')}
                    </span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Lock size={14} /> {t('documents.private_employee_required')}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <Clock size={14} /> {getRetentionInfo(selectedCategory)}
                  </span>
                </div>
              )}
            </div>
            {isOtherCategory(selectedCategory) && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8' }}>
                  {t('documents.designation_label')}
                </label>
                <input
                  type="text"
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  placeholder={t('documents.designation_placeholder')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
                <div style={{ marginTop: '6px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {selectedCategory === t('documents.category_other_public') ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Globe size={14} /> {t('documents.public_accessible_all')}
                    </span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Lock size={14} /> {t('documents.private_accessible_only')}
                    </span>
                  )}
                </div>
              </div>
            )}
            {accessType === 'private' && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8' }}>
                  {t('documents.employee_label')}
                </label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                >
                  <option value="">{t('documents.select_employee')}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id} style={{ color: '#000' }}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8' }}>
                {t('documents.file_label')}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
              {selectedFile && (
                <div style={{ marginTop: '10px', color: '#14b8a6' }}>
                  ✅ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>
            <button
              onClick={handleUpload}
              disabled={
                uploading || 
                !selectedFile || 
                !selectedCategory || 
                (accessType === 'private' && !selectedEmployeeId) ||
                (isOtherCategory(selectedCategory) && !designation.trim())
              }
              style={{
                padding: '14px 24px',
                background: (
                  uploading || 
                  !selectedFile || 
                  !selectedCategory || 
                  (accessType === 'private' && !selectedEmployeeId) ||
                  (isOtherCategory(selectedCategory) && !designation.trim())
                )
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: (
                  uploading || 
                  !selectedFile || 
                  !selectedCategory || 
                  (accessType === 'private' && !selectedEmployeeId) ||
                  (isOtherCategory(selectedCategory) && !designation.trim())
                ) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: (
                  uploading || 
                  !selectedFile || 
                  !selectedCategory || 
                  (accessType === 'private' && !selectedEmployeeId) ||
                  (isOtherCategory(selectedCategory) && !designation.trim())
                ) ? 'none' : '0 4px 12px rgba(20, 184, 166, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!uploading && selectedFile && selectedCategory && (accessType !== 'private' || selectedEmployeeId) && 
                    !(isOtherCategory(selectedCategory) && !designation.trim())) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(20, 184, 166, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading && selectedFile && selectedCategory && (accessType !== 'private' || selectedEmployeeId) &&
                    !(isOtherCategory(selectedCategory) && !designation.trim())) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.3)';
                }
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                  {uploading ? (
                    <span style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid #ffffff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  ) : (
                    <Upload size={18} />
                  )}
                </span>
                <span>{uploading ? t('documents.uploading') : t('documents.upload')}</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
      }}>
        <h2 style={{
          color: '#ffffff',
          marginBottom: '20px',
          fontSize: '20px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FileText size={20} />
          {t('documents.available_documents')} ({documents.length})
        </h2>
        {isAdmin && (
          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '14px',
            marginBottom: '20px',
            padding: '12px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px'
          }}>
            ℹ️ {t('documents.info_admin')}
          </p>
        )}
        {!isAdmin && documents.length > 0 && (
          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '14px',
            marginBottom: '20px',
            padding: '12px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px'
          }}>
            ℹ️ {t('documents.info_employee')}
          </p>
        )}
        {documents.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px'
          }}>
            {t('documents.no_documents')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {documents.map(doc => {
              const expired = isExpired(doc.expiresAt);
              const daysUntilExpiration = getDaysUntilExpiration(doc.expiresAt);
              
              return (
                <div
                  key={doc.id}
                  style={{
                    background: expired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: expired ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '20px',
                    borderRadius: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!expired) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expired) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}>
                        {doc.originalName || doc.name}
                      </div>
                      {doc.accessType === 'public' ? (
                        <Globe size={16} style={{ color: '#14b8a6' }} title={t('documents.public_document')} />
                      ) : (
                        <Lock size={16} style={{ color: '#f59e0b' }} title={t('documents.private_document')} />
                      )}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginBottom: '4px' }}>
                      {getCategoryLabel(doc.category)} • {doc.sizeFormatted || formatFileSize(doc.size)} • {new Date(doc.uploadDate).toLocaleDateString(navigator.language || 'fr-FR')}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                      <User size={14} />
                      {doc.employeeName
                        ? <span>{doc.employeeName}{doc.employeeCode ? ` (${doc.employeeCode})` : ''}</span>
                        : <span>{doc.accessType === 'public' ? t('documents.public_document') : '—'}</span>
                      }
                    </div>
                    {doc.uploadedBy && isAdmin && (
                      <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                        {t('documents.uploaded_by')}: {doc.uploadedBy}
                      </div>
                    )}
                    {doc.expiresAt && (
                      <div style={{ 
                        color: expired ? '#ef4444' : daysUntilExpiration <= 7 ? '#f59e0b' : 'rgba(255, 255, 255, 0.5)', 
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '4px'
                      }}>
                        <Clock size={12} />
                        {expired ? t('documents.expired') : t('documents.expires_in').replace('{{count}}', daysUntilExpiration)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {!expired && (
                      <button
                        onClick={() => handleDownload(doc)}
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(20, 184, 166, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.3)';
                        }}
                      >
                        <Download size={16} />
                        {t('documents.download')}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(doc.id)}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <Trash2 size={16} />
                        {t('documents.delete')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .documents-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .documents-content {
            padding: 180px 60px 60px !important;
          }
        }
        .documents-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DocumentsPage;
